import { useState, useMemo, Fragment } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchChoiceMappings, deleteMapping, createChoiceMapping } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { ChoiceMappingDTO } from "@shared/api-types";
import ChoiceMappingForm from "./choice-mapping-form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Plus,
  Trash2,
  Edit,
  Search,
  X,
  List,
  Layers,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

// Types for sorting
type SortField = 'entityName' | 'fieldName' | 'kupValue' | 'hsValue' | 'hsLabel';
type SortDirection = 'asc' | 'desc';
type ViewMode = 'flat' | 'grouped';

// Helper to safely convert any value to a display string
// Handles [object Object] issues from database corruption
const toDisplayString = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    // Try to extract a meaningful value from the object
    const obj = value as Record<string, unknown>;
    if ('value' in obj) return toDisplayString(obj.value);
    if ('label' in obj) return toDisplayString(obj.label);
    if ('name' in obj) return toDisplayString(obj.name);
    // Last resort: JSON stringify (but truncate if too long)
    try {
      const json = JSON.stringify(value);
      return json.length > 50 ? json.substring(0, 47) + '...' : json;
    } catch {
      return '[complex value]';
    }
  }
  return String(value);
};

interface ChoiceMappingsTableProps {
  onAddMapping: () => void;
}

const ChoiceMappingsTable = ({ onAddMapping }: ChoiceMappingsTableProps) => {
  // Filter state
  const [entityFilter, setEntityFilter] = useState<string>("all_entities");
  const [fieldFilter, setFieldFilter] = useState<string>("all_fields");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Sort state
  const [sortField, setSortField] = useState<SortField>('entityName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('flat');
  const [expandedEntities, setExpandedEntities] = useState<Set<string>>(new Set());

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal state
  const [mappingToEdit, setMappingToEdit] = useState<ChoiceMappingDTO | null>(null);
  const [mappingToDelete, setMappingToDelete] = useState<ChoiceMappingDTO | null>(null);

  // Inline editing state
  const [editingCell, setEditingCell] = useState<{
    rowKey: string;
    field: 'kupValue' | 'hsValue' | 'hsLabel';
    value: string;
  } | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch data
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["/api/choice-mappings", entityFilter, fieldFilter],
    queryFn: () =>
      fetchChoiceMappings(
        entityFilter === "all_entities" ? undefined : entityFilter,
        fieldFilter === "all_fields" ? undefined : fieldFilter,
      ),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: ({ partitionKey, rowKey }: { partitionKey: string; rowKey: string }) =>
      deleteMapping(partitionKey, rowKey),
    onSuccess: () => {
      toast({
        title: "Mapping deleted",
        description: "The choice mapping has been deleted successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/choice-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
      setMappingToDelete(null);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete the mapping.",
        variant: "destructive",
      });
    },
  });

  // Inline update mutation
  const inlineUpdateMutation = useMutation({
    mutationFn: (mapping: ChoiceMappingDTO) => createChoiceMapping({
      type: 'choice',
      entityName: mapping.entityName,
      fieldName: mapping.fieldName,
      kupValue: mapping.kupValue,
      hsValue: mapping.hsValue,
      hsLabel: mapping.hsLabel,
    }),
    onSuccess: () => {
      toast({ title: "Updated", description: "Mapping updated successfully" });
      queryClient.invalidateQueries({ queryKey: ["/api/choice-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
      setEditingCell(null);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update mapping", variant: "destructive" });
    },
  });

  // Get unique values for filters
  const uniqueEntityNames = useMemo(() => {
    return Array.from(new Set(data?.map((mapping) => mapping.entityName) || [])).sort();
  }, [data]);

  const uniqueFieldNames = useMemo(() => {
    return Array.from(new Set(data?.map((mapping) => mapping.fieldName) || [])).sort();
  }, [data]);

  // Filter data
  const filteredData = useMemo(() => {
    if (!data) return [];

    return data.filter((mapping) => {
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = (
          toDisplayString(mapping.entityName).toLowerCase().includes(query) ||
          toDisplayString(mapping.fieldName).toLowerCase().includes(query) ||
          toDisplayString(mapping.kupValue).toLowerCase().includes(query) ||
          toDisplayString(mapping.hsValue).toLowerCase().includes(query) ||
          toDisplayString(mapping.hsLabel).toLowerCase().includes(query)
        );
        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [data, searchQuery]);

  // Sort data
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const aVal = toDisplayString(a[sortField]);
      const bVal = toDisplayString(b[sortField]);
      const comparison = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, sortField, sortDirection]);

  // Group data by entity
  const groupedData = useMemo(() => {
    return sortedData.reduce((acc, mapping) => {
      const entity = mapping.entityName;
      if (!acc[entity]) acc[entity] = [];
      acc[entity].push(mapping);
      return acc;
    }, {} as Record<string, ChoiceMappingDTO[]>);
  }, [sortedData]);

  // Pagination
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  // Handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (type: 'entity' | 'field', value: string) => {
    if (type === 'entity') {
      setEntityFilter(value);
    } else {
      setFieldFilter(value);
    }
    setCurrentPage(1);
  };

  const toggleEntity = (entity: string) => {
    setExpandedEntities(prev => {
      const next = new Set(prev);
      if (next.has(entity)) next.delete(entity);
      else next.add(entity);
      return next;
    });
  };

  const expandAll = () => setExpandedEntities(new Set(Object.keys(groupedData)));
  const collapseAll = () => setExpandedEntities(new Set());

  const handleDeleteClick = (mapping: ChoiceMappingDTO) => {
    setMappingToDelete(mapping);
  };

  const handleConfirmDelete = () => {
    if (!mappingToDelete) return;

    const { partitionKey, rowKey } = mappingToDelete;
    if (!partitionKey || !rowKey) {
      toast({
        title: "Missing identifiers",
        description: "Unable to determine which mapping to delete.",
        variant: "destructive",
      });
      return;
    }

    deleteMutation.mutate({ partitionKey, rowKey });
  };

  const handleEditClick = (mapping: ChoiceMappingDTO) => {
    setMappingToEdit(mapping);
  };

  const closeDeleteDialog = () => setMappingToDelete(null);
  const closeEditModal = () => setMappingToEdit(null);

  const handleInlineEdit = (mapping: ChoiceMappingDTO, field: 'kupValue' | 'hsValue' | 'hsLabel') => {
    setEditingCell({
      rowKey: mapping.rowKey || `${mapping.entityName}_${mapping.fieldName}_${toDisplayString(mapping.kupValue)}`,
      field,
      value: toDisplayString(mapping[field]),
    });
  };

  const handleInlineSave = (mapping: ChoiceMappingDTO, field: 'kupValue' | 'hsValue' | 'hsLabel', newValue: string) => {
    if (newValue !== toDisplayString(mapping[field])) {
      // When saving, ensure all values are strings (not objects)
      inlineUpdateMutation.mutate({
        ...mapping,
        entityName: toDisplayString(mapping.entityName),
        fieldName: toDisplayString(mapping.fieldName),
        kupValue: field === 'kupValue' ? newValue : toDisplayString(mapping.kupValue),
        hsValue: field === 'hsValue' ? newValue : toDisplayString(mapping.hsValue),
        hsLabel: field === 'hsLabel' ? newValue : toDisplayString(mapping.hsLabel),
      });
    } else {
      setEditingCell(null);
    }
  };

  // Sortable header component
  const SortableHeader = ({ field, label }: { field: SortField; label: string }) => (
    <TableHead
      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700 select-none dark:text-zinc-100"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortField === field ? (
          sortDirection === 'asc'
            ? <ChevronUp className="h-4 w-4" />
            : <ChevronDown className="h-4 w-4" />
        ) : (
          <span className="w-4" />
        )}
      </div>
    </TableHead>
  );

  // Render editable cell
  const renderEditableCell = (
    mapping: ChoiceMappingDTO,
    field: 'kupValue' | 'hsValue' | 'hsLabel',
    value: string
  ) => {
    const rowKey = mapping.rowKey || `${mapping.entityName}_${mapping.fieldName}_${toDisplayString(mapping.kupValue)}`;
    const isEditing = editingCell?.rowKey === rowKey && editingCell?.field === field;

    if (isEditing) {
      return (
        <Input
          autoFocus
          defaultValue={editingCell.value}
          className="h-8 w-full dark:bg-zinc-800 dark:border-zinc-600"
          onBlur={(e) => handleInlineSave(mapping, field, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleInlineSave(mapping, field, e.currentTarget.value);
            } else if (e.key === 'Escape') {
              setEditingCell(null);
            }
          }}
        />
      );
    }

    return (
      <span
        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700 px-2 py-1 rounded inline-block min-w-[60px]"
        onClick={() => handleInlineEdit(mapping, field)}
        title="Click to edit"
      >
        {value || <span className="text-gray-400 italic">empty</span>}
      </span>
    );
  };

  // Render row content
  const renderRowContent = (mapping: ChoiceMappingDTO, showEntity: boolean = true) => {
    return (
      <>
        <TableCell className={`dark:text-zinc-100 ${!showEntity ? 'pl-8' : ''}`}>
          {showEntity ? toDisplayString(mapping.entityName) : '—'}
        </TableCell>
        <TableCell className="dark:text-zinc-100">
          {toDisplayString(mapping.fieldName)}
        </TableCell>
        <TableCell className="dark:text-zinc-100">
          {renderEditableCell(mapping, 'kupValue', toDisplayString(mapping.kupValue))}
        </TableCell>
        <TableCell className="dark:text-zinc-100">
          {renderEditableCell(mapping, 'hsValue', toDisplayString(mapping.hsValue))}
        </TableCell>
        <TableCell className="dark:text-zinc-100">
          {renderEditableCell(mapping, 'hsLabel', toDisplayString(mapping.hsLabel))}
        </TableCell>
        <TableCell className="text-right space-x-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleEditClick(mapping)}
            className="dark:text-zinc-300 dark:hover:text-white"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleDeleteClick(mapping)}
            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </TableCell>
      </>
    );
  };

  if (isError) {
    return (
      <div className="bg-white dark:bg-zinc-900 rounded-lg shadow p-2 mb-6">
        <h3 className="text-red-500 dark:text-red-400">Error loading choice mappings: {String(error)}</h3>
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        {/* Header with title and controls */}
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <h2 className="text-xl font-semibold dark:text-zinc-100">Choice Mappings</h2>
            <Button onClick={onAddMapping} className="flex gap-1 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600">
              <Plus size={16} />
              Add Mapping
            </Button>
          </div>

          {/* Search and filters row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search mappings..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 w-[200px] dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                  onClick={() => handleSearchChange("")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Entity Filter */}
            <Select value={entityFilter} onValueChange={(v) => handleFilterChange('entity', v)}>
              <SelectTrigger className="w-[180px] dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100">
                <SelectValue placeholder="Filter by entity" />
              </SelectTrigger>
              <SelectContent className="dark:bg-zinc-800 dark:text-zinc-100">
                <SelectItem value="all_entities">All Entities</SelectItem>
                {uniqueEntityNames.map((entity) => (
                  <SelectItem key={entity} value={entity} className="dark:text-zinc-100">
                    {entity}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Field Filter */}
            <Select value={fieldFilter} onValueChange={(v) => handleFilterChange('field', v)}>
              <SelectTrigger className="w-[180px] dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100">
                <SelectValue placeholder="Filter by field" />
              </SelectTrigger>
              <SelectContent className="dark:bg-zinc-800 dark:text-zinc-100">
                <SelectItem value="all_fields">All Fields</SelectItem>
                {uniqueFieldNames.map((field) => (
                  <SelectItem key={field} value={field} className="dark:text-zinc-100">
                    {field}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* View mode toggle */}
            <div className="flex items-center gap-1 ml-auto">
              <Button
                variant={viewMode === 'flat' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('flat')}
                className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
              >
                <List className="h-4 w-4 mr-1" /> Flat
              </Button>
              <Button
                variant={viewMode === 'grouped' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grouped')}
                className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
              >
                <Layers className="h-4 w-4 mr-1" /> Grouped
              </Button>
              {viewMode === 'grouped' && (
                <>
                  <Button variant="ghost" size="sm" onClick={expandAll} className="dark:text-zinc-300">
                    Expand All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={collapseAll} className="dark:text-zinc-300">
                    Collapse All
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Results count */}
          <div className="text-sm text-gray-500 dark:text-zinc-400">
            {filteredData.length} result{filteredData.length !== 1 ? 's' : ''}
            {searchQuery && ` for "${searchQuery}"`}
            {entityFilter !== 'all_entities' && ` in ${entityFilter}`}
            {fieldFilter !== 'all_fields' && ` (${fieldFilter})`}
          </div>
        </div>

        {/* Table */}
        <div className="rounded-md border dark:border-zinc-700">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50 dark:bg-zinc-800">
                <SortableHeader field="entityName" label="Entity" />
                <SortableHeader field="fieldName" label="Field" />
                <SortableHeader field="kupValue" label="KUP Value" />
                <SortableHeader field="hsValue" label="HS Value" />
                <SortableHeader field="hsLabel" label="HS Label" />
                <TableHead className="text-right dark:text-zinc-100">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array(5).fill(0).map((_, i) => (
                  <TableRow key={i} className="dark:bg-zinc-900">
                    {Array(6).fill(0).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-20 dark:bg-zinc-700" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : viewMode === 'grouped' ? (
                // Grouped view
                Object.entries(groupedData).length === 0 ? (
                  <TableRow className="dark:bg-zinc-900">
                    <TableCell colSpan={6} className="text-center py-4 dark:text-zinc-300">
                      No choice mappings found
                    </TableCell>
                  </TableRow>
                ) : (
                  Object.entries(groupedData).map(([entity, mappings]) => (
                    <Fragment key={entity}>
                      <TableRow
                        className="bg-gray-100 dark:bg-zinc-800 cursor-pointer hover:bg-gray-200 dark:hover:bg-zinc-700"
                        onClick={() => toggleEntity(entity)}
                      >
                        <TableCell colSpan={6} className="font-semibold dark:text-zinc-100">
                          <div className="flex items-center gap-2">
                            {expandedEntities.has(entity)
                              ? <ChevronDown className="h-4 w-4" />
                              : <ChevronRight className="h-4 w-4" />
                            }
                            {entity}
                            <Badge variant="secondary" className="dark:bg-zinc-600 dark:text-zinc-200">
                              {mappings.length}
                            </Badge>
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedEntities.has(entity) && mappings.map(mapping => (
                        <TableRow
                          key={mapping.rowKey || `${mapping.entityName}_${mapping.fieldName}_${toDisplayString(mapping.kupValue)}`}
                          className="dark:bg-zinc-900"
                        >
                          {renderRowContent(mapping, false)}
                        </TableRow>
                      ))}
                    </Fragment>
                  ))
                )
              ) : (
                // Flat view with pagination
                paginatedData.length === 0 ? (
                  <TableRow className="dark:bg-zinc-900">
                    <TableCell colSpan={6} className="text-center py-4 dark:text-zinc-300">
                      No choice mappings found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((mapping) => (
                    <TableRow
                      key={mapping.rowKey || `${mapping.entityName}_${mapping.fieldName}_${toDisplayString(mapping.kupValue)}`}
                      className="dark:bg-zinc-900"
                    >
                      {renderRowContent(mapping, true)}
                    </TableRow>
                  ))
                )
              )}
            </TableBody>
          </Table>

          {/* Pagination (only in flat view) */}
          {viewMode === 'flat' && sortedData.length > itemsPerPage && (
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-zinc-800">
              <div className="text-sm text-gray-700 dark:text-zinc-200">
                Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
                <span className="font-medium">{Math.min(endIndex, totalItems)}</span>{" "}
                of <span className="font-medium">{totalItems}</span> results
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage <= 1}
                  className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage >= totalPages}
                  className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
                >
                  <ChevronRight className="h-4 w-4" />
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Delete confirmation dialog */}
        {mappingToDelete && (
          <AlertDialog open={!!mappingToDelete} onOpenChange={closeDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete the mapping for{" "}
                  <strong>
                    {mappingToDelete.entityName}.{mappingToDelete.fieldName}
                  </strong>
                  ? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirmDelete}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {/* Edit form modal */}
        {mappingToEdit && (
          <ChoiceMappingForm
            mapping={mappingToEdit}
            isOpen={!!mappingToEdit}
            onClose={closeEditModal}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ["/api/choice-mappings"] });
              queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
              closeEditModal();
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ChoiceMappingsTable;
